import z from 'zod'
import { isEmpty } from '../../util/isEmpty'

export const LatLngSchema = z
  .object({
    latitude: z
      .number()
      .min(-90)
      .max(90)
      .meta({ id: 'latitude', title: 'Latitude', description: 'Geographical latitude', example: 33.7 }),
    longitude: z
      .number()
      .min(-180)
      .max(180)
      .meta({ id: 'longitude', title: 'Longitude', description: 'Geographical longitude', example: -117.8 }),
  })
  .meta({ id: 'latlng', title: 'Latitude and Longitude', description: 'Geographical coordinates' })
export type LatLng = z.infer<typeof LatLngSchema>

export type LatLngBoundingBox = {
  top: LatLng
  bottom: LatLng
}

export const LAT_LNG_BOUNDING_BOX_ERROR_MESSAGE =
  'Bounding box top latitude must be greater than bottom latitude and longitudes must differ'

export function isValidLatLngBoundingBox({ top, bottom }: LatLngBoundingBox): boolean {
  if (top.latitude <= bottom.latitude) return false
  return top.longitude !== bottom.longitude
}

export const LatLngBoundingBoxSchema = z
  .object({
    top: LatLngSchema,
    bottom: LatLngSchema,
  })
  .refine(isValidLatLngBoundingBox, { message: LAT_LNG_BOUNDING_BOX_ERROR_MESSAGE })

export type WithLatLng = {
  latlng: LatLng
}

export const AltLatLngSchema = z.object({
  ...LatLngSchema.shape,
  source: z.string(),
})
export type AltLatLng = z.infer<typeof AltLatLngSchema>

export const PartialLatLngSchema = z.object({
  latitude: LatLngSchema.shape.latitude.nullable().optional(),
  longitude: LatLngSchema.shape.longitude.nullable().optional(),
})
export type PartialLatLng = z.infer<typeof PartialLatLngSchema>

export const UpsertLatLngSchema = z.object({
  latitude: z
    .union([z.string(), z.number()])
    .optional()
    .meta({ description: 'Geographical latitude. Accepts a number or a numeric string.', example: 33.7 }),
  longitude: z
    .union([z.string(), z.number()])
    .optional()
    .meta({ description: 'Geographical longitude. Accepts a number or a numeric string.', example: -117.8 }),
})

export function isLatLngEmpty(latlng: LatLng | null | undefined): boolean {
  if (isEmpty(latlng)) return true
  return isEmpty(latlng.latitude) || isEmpty(latlng.longitude)
}

export const QuadrantIdSchema = z
  .templateLiteral([
    'Q',
    ':',
    z.number().int().describe('zoomLevel'),
    ':',
    z.number().int().describe('xIndex'),
    ',',
    z.number().int().describe('yIndex'),
  ])
  .meta({
    description: 'Map tile (quadrant) ID in the form `Q:<zoomLevel>:<xIndex>,<yIndex>`, e.g. `Q:14:2816,6542`',
    example: 'Q:14:2816,6542',
  })
export type QuadrantId = z.infer<typeof QuadrantIdSchema>
